import React, { useEffect } from "react";
import { Helmet } from "react-helmet";

const styles = {
  page: {
    fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
    background: "#0F0F0F",
    color: "#FFFFFF",
    margin: 0,
    padding: 0,
    overflowX: "hidden",
  },
  hero: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "0 6vw",
    background: "linear-gradient(135deg, #0F0F0F 0%, #1A1A1A 60%, #120820 100%)",
    borderBottom: "1px solid #2a2a2a",
    overflow: "hidden",
  },
  heroAccent: {
    position: "absolute",
    top: "-120px",
    right: "-80px",
    width: "480px",
    height: "480px",
    background: "radial-gradient(circle, rgba(123,25,188,0.18) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(123,25,188,0.12)",
    border: "1px solid rgba(123,25,188,0.35)",
    borderRadius: "100px",
    padding: "6px 16px",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: "#7b19bc",
    textTransform: "uppercase",
    marginBottom: "28px",
    width: "fit-content",
  },
  heroDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#7b19bc",
    animation: "pulse 2s infinite",
  },
  heroTitle: {
    fontSize: "clamp(2.8rem, 6vw, 5.5rem)",
    fontWeight: 900,
    lineHeight: 1.02,
    letterSpacing: "-0.03em",
    margin: "0 0 12px 0",
    maxWidth: "820px",
  },
  heroOrange: {
    color: "#7b19bc",
  },
  heroSub: {
    fontSize: "clamp(1rem, 1.8vw, 1.2rem)",
    color: "#ccc",
    maxWidth: "520px",
    lineHeight: 1.65,
    margin: "20px 0 0 0",
  },
  heroMeta: {
    display: "flex",
    gap: "40px",
    marginTop: "52px",
    flexWrap: "wrap",
  },
  heroMetaItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  heroMetaLabel: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#555",
  },
  heroMetaValue: {
    fontSize: "15px",
    fontWeight: 500,
    color: "#ccc",
  },
  sectionDark: {
    padding: "100px 6vw",
    background: "#0F0F0F",
    color: "#FFFFFF",
  },
  sectionLight: {
    padding: "100px 6vw",
    background: "#FFFFFF",
    borderTop: "1px solid #e5e5e5",
    borderBottom: "1px solid #e5e5e5",
  },
  eyebrow: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#7b19bc",
    marginBottom: "18px",
  },
  h2: {
    fontSize: "clamp(1.8rem, 3.5vw, 3rem)",
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1.1,
    margin: "0 0 24px 0",
    maxWidth: "680px",
    color: "#FFFFFF",
  },
  body: {
    fontSize: "1.05rem",
    color: "#bbb",
    lineHeight: 1.8,
    maxWidth: "620px",
  },
  eyebrowDark: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#7b19bc",
    marginBottom: "18px",
  },
  h2Dark: {
    fontSize: "clamp(1.8rem, 3.5vw, 3rem)",
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1.1,
    margin: "0 0 24px 0",
    maxWidth: "680px",
    color: "#111",
  },
  problemGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0",
    marginTop: "60px",
    border: "1px solid #2a2a2a",
    borderRadius: "4px",
    overflow: "hidden",
  },
  problemCard: {
    padding: "40px",
    borderRight: "1px solid #2a2a2a",
    background: "#161616",
  },
  problemCardRight: {
    padding: "40px",
    background: "#161616",
  },
  problemCardTitleBad: {
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: "20px",
    color: "#9b1515",
  },
  problemCardTitleGood: {
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: "20px",
    color: "#22aa55",
  },
  problemItem: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    marginBottom: "16px",
    fontSize: "14px",
    color: "#aaa",
    lineHeight: 1.5,
  },
  iconBad: { color: "#9b1515", fontWeight: 700, flexShrink: 0, marginTop: "1px" },
  iconGood: { color: "#22aa55", fontWeight: 700, flexShrink: 0, marginTop: "1px" },
  resultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    marginTop: "56px",
  },
  resultCard: {
    background: "#f9f9f9",
    border: "1px solid #e5e5e5",
    borderRadius: "4px",
    padding: "36px",
  },
  resultCardHighlight: {
    background: "rgba(123,25,188,0.06)",
    border: "1px solid rgba(123,25,188,0.25)",
    borderRadius: "4px",
    padding: "36px",
  },
  resultNum: {
    fontSize: "3rem",
    fontWeight: 900,
    letterSpacing: "-0.04em",
    color: "#7b19bc",
    lineHeight: 1,
    marginBottom: "8px",
  },
  resultLabel: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#111",
    marginBottom: "8px",
  },
  resultDesc: {
    fontSize: "13px",
    color: "#555",
    lineHeight: 1.55,
  },
  ctaSection: {
    padding: "100px 6vw",
    textAlign: "center",
    background: "#0F0F0F",
    borderTop: "1px solid #1e1e1e",
  },
  ctaSub: {
    fontSize: "1.05rem",
    color: "#666",
    marginBottom: "40px",
    maxWidth: "480px",
    margin: "0 auto 40px auto",
    lineHeight: 1.65,
  },
  ctaBtn: {
    display: "inline-block",
    background: "#7b19bc",
    color: "#fff",
    padding: "16px 40px",
    borderRadius: "4px",
    fontSize: "15px",
    fontWeight: 700,
    textDecoration: "none",
    letterSpacing: "0.02em",
    border: "none",
    cursor: "pointer",
    transition: "background 0.2s, transform 0.15s",
  },
  footerStrip: {
    borderTop: "1px solid #1a1a1a",
    padding: "28px 6vw",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "12px",
    color: "#444",
  },
};

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      "@id": "https://www.reflexadvisor.com/junkremovaltorontocasestudy#article",
      "headline": "From Invisible to Fully Booked — Junk Removal in Toronto Case Study",
      "description": "How Reflex Advisor built a complete digital growth system for Junk Removal in Toronto — turning a brand new business into a consistent source of same-day bookings.",
      "image": "https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/Dark-Logo.webp",
      "author": {
        "@type": "Organization",
        "name": "Reflex Advisor",
        "url": "https://www.reflexadvisor.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Reflex Advisor",
        "url": "https://www.reflexadvisor.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.reflexadvisor.com/logo.png"
        }
      },
      "datePublished": "2025-06-01",
      "dateModified": "2025-06-01",
      "mainEntityOfPage": "https://www.reflexadvisor.com/junkremovaltorontocasestudy"
    },
    {
      "@type": "Organization",
      "@id": "https://www.reflexadvisor.com#organization",
      "name": "Reflex Advisor",
      "url": "https://www.reflexadvisor.com",
      "description": "Reflex Advisor delivers done-for-you lead generation and sales automation for service-based businesses in Canada.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Toronto",
        "addressRegion": "Ontario",
        "addressCountry": "CA"
      }
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.reflexadvisor.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Success Stories",
          "item": "https://www.reflexadvisor.com/successstories"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Junk Removal Toronto Case Study",
          "item": "https://www.reflexadvisor.com/junkremovaltorontocasestudy"
        }
      ]
    }
  ]
};

export default function JunkRemovalTorontoCaseStudy() {

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div style={styles.page}>

      <Helmet>
        <title>Junk Removal Toronto Case Study | Reflex Advisor — Same-Day Bookings from Scratch</title>
        <meta name="description" content="See how Reflex Advisor launched Junk Removal in Toronto from zero — building a converting website, local SEO, and CRM automation that drives same-day bookings across the GTA." />
        <meta name="keywords" content="Reflex Advisor case study, junk removal Toronto marketing, lead generation junk removal GTA, service business growth Canada, CRM automation Toronto, local SEO junk removal Scarborough" />
        <link rel="canonical" href="https://www.reflexadvisor.com/junkremovaltorontocasestudy" />

        <meta property="og:type" content="article" />
        <meta property="og:title" content="Junk Removal Toronto Case Study | Reflex Advisor — Same-Day Bookings from Scratch" />
        <meta property="og:description" content="How Reflex Advisor built Junk Removal in Toronto's entire digital presence from the ground up — website, automation, and a pipeline that turns visitors into same-day bookings." />
        <meta property="og:url" content="https://www.reflexadvisor.com/junkremovaltorontocasestudy" />
        <meta property="og:image" content="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/Dark-Logo.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Reflex Advisor" />
        <meta property="og:locale" content="en_CA" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Junk Removal Toronto Case Study | Reflex Advisor" />
        <meta name="twitter:description" content="From zero online presence to same-day bookings across the GTA. See how Reflex Advisor built the full growth system for Junk Removal in Toronto." />
        <meta name="twitter:image" content="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/Dark-Logo.webp" />

        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="Reflex Advisor" />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* ── Hero (dark) ── */}
      <section style={styles.hero} aria-label="Case study overview">
        <div style={styles.heroAccent} />

        <div style={styles.heroBadge}>
          <span style={styles.heroDot} />
          Case Study — Junk Removal · Toronto, ON
        </div>
        <h1 style={styles.heroTitle}>
          From Brand New to{" "}
          <span style={styles.heroOrange}>Same-Day Booked</span>{" "}
          Across the GTA
        </h1>
        <p style={styles.heroSub}>
          How Reflex Advisor launched Junk Removal in Toronto from scratch —
          building a high-converting website, automated pipeline, and local SEO
          foundation that drives consistent same-day bookings without paid ads.
        </p>
        <div style={styles.heroMeta}>
          {[
            { label: "Client", value: "Junk Removal in Toronto" },
            { label: "Location", value: "Scarborough, Toronto, ON" },
            { label: "Coverage", value: "Toronto, GTA, York & Durham" },
            { label: "Website", value: "junkremovalintoronto.ca" },
          ].map((m) => (
            <div key={m.label} style={styles.heroMetaItem}>
              <span style={styles.heroMetaLabel}>{m.label}</span>
              <span style={styles.heroMetaValue}>{m.value}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Problem (dark) ── */}
      <section style={styles.sectionDark} aria-label="The problem before Reflex Advisor">
        <div style={styles.eyebrow}>The Problem</div>
        <h2 style={styles.h2}>
          A capable team. No digital presence whatsoever.
        </h2>
        <p style={styles.body}>
          Junk Removal in Toronto launched with the right setup — licensed,
          insured, same-day capability, and competitive pricing. But without a
          website, local search visibility, or any kind of follow-up system,
          the phones stayed quiet. Every potential customer searching "junk
          removal Toronto" was finding competitors instead. The business needed
          a complete digital foundation built fast.
        </p>

        <div style={styles.problemGrid}>
          <div style={styles.problemCard}>
            <div style={styles.problemCardTitleBad}>Before Reflex</div>
            {[
              "No website — the business was invisible online",
              "Zero Google Business Profile or local directory presence",
              "No way for customers to request a quote outside of a phone call",
              "Leads tracked manually with no pipeline or follow-up",
              "Competitors dominated every 'junk removal Toronto' search",
              "Same-day capacity sat unused due to lack of inbound enquiries",
            ].map((item) => (
              <div key={item} style={styles.problemItem}>
                <span style={styles.iconBad}>✕</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div style={styles.problemCardRight}>
            <div style={styles.problemCardTitleGood}>After Reflex</div>
            {[
              "Professional site ranking for Toronto & neighbourhood keywords",
              "Fully optimised Google Business Profile with verified listings",
              "Quote request forms converting visitors 24/7, including mobile",
              "CRM pipeline tracking every lead from first contact to job done",
              "Neighbourhood landing pages pulling targeted traffic across the GTA",
              "Automated follow-up re-engaging every lead that didn't book",
            ].map((item) => (
              <div key={item} style={styles.problemItem}>
                <span style={styles.iconGood}>✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What We Built (light) ── */}
      <section style={styles.sectionLight} aria-label="What Reflex Advisor built for Junk Removal in Toronto">
        <div style={styles.eyebrowDark}>What We Built</div>
        <h2 style={styles.h2Dark}>
          A full growth system. Built to run without babysitting.
        </h2>

        <div style={{ marginTop: "56px", display: "flex", flexDirection: "column", alignItems: "center", gap: "0" }}>
          {[
            { icon: "🌐", title: "High-Converting Website", desc: "Mobile-first site with service pages, neighbourhood landing pages & quote forms" },
            { icon: "📍", title: "Local SEO & GBP Setup", desc: "Google Business Profile + 15+ directory listings targeting Scarborough, Etobicoke, North York & more" },
            { icon: "⚡", title: "Instant Quote Response", desc: "Automated reply sent within 60 seconds of every online enquiry" },
            { icon: "🤖", title: "CRM & Lead Pipeline", desc: "New Lead → Quoted → Booked → Completed, tracked automatically end-to-end" },
            { icon: "📨", title: "Follow-Up Sequences", desc: "Multi-step cadence that re-engages leads who didn't book within 14 days" },
            { icon: "📊", title: "Live Sales Dashboard", desc: "One screen showing all jobs, pipeline value, lead volume & response stats" },
          ].map((s, i) => (
            <div key={s.title} style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                width: "100%",
                maxWidth: "760px",
              }}>
                {i % 2 === 0 ? (
                  <>
                    <div style={{ textAlign: "right", paddingRight: "32px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: "#111", marginBottom: "4px" }}>{s.title}</div>
                      <div style={{ fontSize: "12px", color: "#666", lineHeight: 1.5 }}>{s.desc}</div>
                    </div>
                    <div style={{
                      width: "52px", height: "52px", borderRadius: "50%",
                      background: "rgba(123,25,188,0.08)", border: "1.5px solid rgba(123,25,188,0.4)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "22px", flexShrink: 0, zIndex: 1,
                    }}>{s.icon}</div>
                    <div style={{ paddingLeft: "32px" }} />
                  </>
                ) : (
                  <>
                    <div style={{ paddingRight: "32px" }} />
                    <div style={{
                      width: "52px", height: "52px", borderRadius: "50%",
                      background: "rgba(123,25,188,0.08)", border: "1.5px solid rgba(123,25,188,0.4)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "22px", flexShrink: 0, zIndex: 1,
                    }}>{s.icon}</div>
                    <div style={{ textAlign: "left", paddingLeft: "32px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: "#111", marginBottom: "4px" }}>{s.title}</div>
                      <div style={{ fontSize: "12px", color: "#666", lineHeight: 1.5 }}>{s.desc}</div>
                    </div>
                  </>
                )}
              </div>
              {i < 5 && (
                <div style={{
                  width: "2px", height: "36px",
                  background: "linear-gradient(to bottom, rgba(123,25,188,0.5), rgba(123,25,188,0.1))",
                }} />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Timeline (dark) ── */}
      <section style={styles.sectionDark} aria-label="Project timeline">
        <div style={styles.eyebrow}>The Process</div>
        <h2 style={styles.h2}>Built from zero. Structured from day one.</h2>

        <div style={{ marginTop: "60px", position: "relative" }}>
          {[
            { week: "Week 1", title: "Discovery & Strategy", desc: "Mapped the service area, identified high-value neighbourhoods, and researched competitor gaps to position the brand for immediate traction." },
            { week: "Week 2", title: "Website Design & Build", desc: "Launched a mobile-first site with a homepage, five service pages, and neighbourhood landing pages for Scarborough, Etobicoke, North York, and downtown Toronto." },
            { week: "Weeks 3–4", title: "CRM Setup & Automation", desc: "Pipeline stages configured, quote forms connected, instant-response automations activated, and a 14-day follow-up sequence loaded and tested." },
            { week: "Week 5", title: "Local SEO & Directory Listings", desc: "Google Business Profile verified and fully optimised. 15+ local directory listings created. Neighbourhood pages submitted for indexing." },
            { week: "Weeks 6–8", title: "Launch, Monitor & Hand Over", desc: "Went live, tracked lead flow, refined conversion points, and handed over a live dashboard the owner can manage independently from day one." },
          ].map((item, i) => {
            const isLeft = i % 2 === 0;
            return (
              <div key={item.week} style={{ display: "flex", flexDirection: "column", alignItems: isLeft ? "flex-start" : "flex-end", marginBottom: "0" }}>
                <div style={{ display: "flex", alignItems: "center", width: "100%", position: "relative" }}>
                  <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", paddingRight: "24px" }}>
                    {!isLeft && (
                      <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: "4px", padding: "20px 24px", maxWidth: "320px", textAlign: "right" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7b19bc", marginBottom: "4px" }}>{item.week}</div>
                        <div style={{ fontSize: "15px", fontWeight: 700, color: "#e0e0e0", marginBottom: "6px" }}>{item.title}</div>
                        <div style={{ fontSize: "13px", color: "#888", lineHeight: 1.6 }}>{item.desc}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "#7b19bc", border: "2px solid #0F0F0F", boxShadow: "0 0 0 3px rgba(123,25,188,0.25)", flexShrink: 0, zIndex: 1 }} />
                  <div style={{ flex: 1, paddingLeft: "24px" }}>
                    {isLeft && (
                      <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: "4px", padding: "20px 24px", maxWidth: "320px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7b19bc", marginBottom: "4px" }}>{item.week}</div>
                        <div style={{ fontSize: "15px", fontWeight: 700, color: "#e0e0e0", marginBottom: "6px" }}>{item.title}</div>
                        <div style={{ fontSize: "13px", color: "#888", lineHeight: 1.6 }}>{item.desc}</div>
                      </div>
                    )}
                  </div>
                </div>
                {i < 4 && (
                  <div style={{ width: "50%", display: "flex", justifyContent: isLeft ? "flex-end" : "flex-start", alignSelf: "stretch" }}>
                    <div style={{
                      borderRight: isLeft ? "2px solid rgba(123,25,188,0.35)" : "none",
                      borderLeft: !isLeft ? "2px solid rgba(123,25,188,0.35)" : "none",
                      borderBottom: "2px solid rgba(123,25,188,0.35)",
                      borderRadius: isLeft ? "0 0 12px 0" : "0 0 0 12px",
                      width: "50%",
                      height: "48px",
                      marginRight: isLeft ? "-1px" : "0",
                      marginLeft: !isLeft ? "-1px" : "0",
                    }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Results (light) ── */}
      <section style={styles.sectionLight} aria-label="Results achieved for Junk Removal in Toronto">
        <div style={styles.eyebrowDark}>The Results</div>
        <h2 style={styles.h2Dark}>Numbers that prove the system works.</h2>

        <div style={styles.resultsGrid}>
          <div style={styles.resultCardHighlight}>
            <div style={styles.resultNum}>Same Day</div>
            <div style={styles.resultLabel}>Bookings from organic traffic — no ad spend</div>
            <div style={styles.resultDesc}>
              The website began converting same-day enquiries within the first
              weeks of launch. Neighbourhood landing pages pulled targeted
              traffic from Scarborough, Etobicoke, and North York — customers
              actively looking to book that day.
            </div>
          </div>
          <div style={styles.resultCard}>
            <div style={styles.resultNum}>&lt;2 min</div>
            <div style={styles.resultLabel}>Average lead response time</div>
            <div style={styles.resultDesc}>
              Every enquiry submitted through the website receives an automated,
              personalised response within 60–120 seconds — putting the business
              ahead of every competitor who replies hours later.
            </div>
          </div>
          <div style={styles.resultCard}>
            <div style={styles.resultNum}>15+</div>
            <div style={styles.resultLabel}>Local directory & SEO touchpoints live at launch</div>
            <div style={styles.resultDesc}>
              Google Business Profile, Yelp, Homestars, and 12+ additional
              directories were set up and verified, building local authority and
              expanding the business's footprint across the GTA from day one.
            </div>
          </div>
          <div style={styles.resultCardHighlight}>
            <div style={styles.resultNum}>0 Lost</div>
            <div style={styles.resultLabel}>Leads falling through the cracks</div>
            <div style={styles.resultDesc}>
              Every enquiry enters the CRM automatically. No missed calls, no
              forgotten WhatsApp messages, no cold leads going cold forever.
              The pipeline is live, visible, and always accurate.
            </div>
          </div>
        </div>
      </section>

      {/* ── Project Gallery (dark) ── */}
      <section style={styles.sectionDark} aria-label="Project gallery">
        <div style={styles.eyebrow}>Project Gallery</div>
        <h2 style={styles.h2}>A look at what we delivered.</h2>
        <p style={{ ...styles.body, marginBottom: "0" }}>
          From the live website to the backend CRM dashboard — here's a snapshot
          of the system built for Junk Removal in Toronto.
        </p>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "20px",
          marginTop: "52px",
        }}>
          {[
            {
              src: "https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/junkremoval.png",
              label: "Website",
              caption: "Live website — junkremovalintoronto.ca",
            },
            {
              src: "https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/dashboard%20daily%20calls.png",
              label: "CRM Dashboard",
              caption: "Daily calls & lead pipeline overview",
            },
          ].map((img) => (
            <div
              key={img.label}
              style={{
                background: "#161616",
                border: "1px solid #2a2a2a",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
             <div style={{
  width: "100%",
  background: "#111",
  overflow: "hidden",
}}>
  <img
    src={img.src}
    alt={img.caption}
    style={{
      width: "100%",
      height: "auto",
      display: "block",
    }}
  />
</div>
              <div style={{ padding: "20px 24px" }}>
                <div style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#7b19bc",
                  marginBottom: "4px",
                }}>
                  {img.label}
                </div>
                <div style={{ fontSize: "13px", color: "#888", lineHeight: 1.5 }}>
                  {img.caption}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA (dark) ── */}
      <section style={styles.ctaSection} aria-label="Book a free strategy call with Reflex Advisor">
        <h2 style={{ ...styles.h2, maxWidth: "none", textAlign: "center", margin: "0 auto 20px auto" }}>
          Ready to grow your{" "}
          <span style={styles.heroOrange}>service business?</span>
        </h2>
        <p style={styles.ctaSub}>
          We work with home service companies across Canada — junk removal,
          landscaping, cleaning, moving, and more. Let's build your growth
          engine.
        </p>
        <a
          href="https://www.reflexadvisor.com"
          style={styles.ctaBtn}
          onMouseEnter={(e) => {
            e.target.style.background = "#6512a0";
            e.target.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#7b19bc";
            e.target.style.transform = "translateY(0)";
          }}
        >
          Book a Free Strategy Call →
        </a>
      </section>

      {/* ── Footer strip ── */}
      <footer style={styles.footerStrip} aria-label="Page footer">
        <span>© {new Date().getFullYear()} Reflex Advisor — reflexadvisor.com</span>
        <span>Case Study: Junk Removal in Toronto</span>
      </footer>

    </div>
  );
}